import { Container, Grid, makeStyles, Typography } from "@material-ui/core";
import { useAssignInfo } from "@saleor/accountSettings/misc";
import { auditLogsExpressFleet } from "@saleor/auditLogs/urls";
import ExpressFleetOpsFormCard from "@saleor/autobooking/components/ExpressFleetOpsFormCard";
import { useSetMallAutoBookingConfig } from "@saleor/autobooking/mutations";
import { useExpressFleetMallsConfig } from "@saleor/autobooking/queries";
import { expressFleetCourierChoices } from "@saleor/autobooking/utils/handlers";
import AutoBookingConfig from "@saleor/autobooking/utils/objects";
import MoreActions from "@saleor/components/MoreActions";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { configurationOpsMenuUrl } from "@saleor/configuration_operations";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { CourierFilter, StoreTypeEnum } from "@saleor/types/globalTypes";
import React, { useState } from "react";
import { useIntl } from "react-intl";

const useStyles = makeStyles(
  _ => ({
    inputCard: {
      display: "flex",
      flexWrap: "wrap",
      gap: "10px",
      justifyContent: "center"
    },
    mallNameLabel: {
      fontSize: "23px",
      fontWeight: 600,
      marginBottom: "25px"
    },
    pageHeader: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      marginTop: "10px"
    }
  }),
  { name: "ExpressFleetOpsPage" }
);

const ExpressFleetOpsPage = () => {
  const classes = useStyles("");
  const notify = useNotifier();
  const navigate = useNavigator();
  const intl = useIntl();
  const user = useUser();
  const userInfo = useAssignInfo(user?.user);

  const [courier, setCourier] = useState({
    fb: CourierFilter.PANDAGO_AUTO || CourierFilter.GRAB_EXPRESS,
    gm: CourierFilter.PANDAGO_AUTO || CourierFilter.GRAB_EXPRESS
  });
  const [fnbAutoBooking, setFnbAutoBooking] = useState<AutoBookingConfig>(null);
  const [gmAutoBooking, setGmAutoBooking] = useState<AutoBookingConfig>(null);
  const [fbEnabled, setFbEnabled] = useState(false);
  const [gmEnabled, setGmEnabled] = useState(false);
  const [editOn, setEditOn] = useState(false);
  const [fbFormUpdated, setFbFormUpdated] = useState(false);
  const [gmFormUpdated, setGmFormUpdated] = useState(false);
  const [fbDefaultValues, setFbDefaultValues] = useState(null);
  const [gmDefaultValues, setGmDefaultValues] = useState(null);
  const [saveIndicator, setSaveIndicator] = useState("");

  const resetForm = () => {
    setEditOn(false);
    setFbFormUpdated(false);
    setGmFormUpdated(false);
    setFnbAutoBooking(mall?.fbAutoBookingConfig);
    setFbEnabled(mall?.fbAutoBookingConfig?.isEnabled || false);
    setGmAutoBooking(mall?.gmAutoBookingConfig);
    setGmEnabled(mall?.gmAutoBookingConfig?.isEnabled || false);
    setCourier({
      fb: mall?.fbAutoBookingConfig?.courier,
      gm: mall?.gmAutoBookingConfig?.courier
    });

    setFbDefaultValues({
      autoBookingConfig: mall?.fbAutoBookingConfig,
      courier: mall?.fbAutoBookingConfig?.courier || "",
      enabled: mall?.fbAutoBookingConfig?.isEnabled || false
    });
    setGmDefaultValues({
      autoBookingConfig: mall?.gmAutoBookingConfig,
      courier: mall?.gmAutoBookingConfig?.courier || "",
      enabled: mall?.gmAutoBookingConfig?.isEnabled || false
    });
    setSaveIndicator("");
  };

  const breadcrumbDirectory = [
    {
      name: "Configuration",
      url: "/operations"
    },
    {
      name: "Express Fleet"
    }
  ];

  const { data, refetch } = useExpressFleetMallsConfig({
    displayLoader: true,
    variables: {
      first: 100,
      id: [userInfo?.mall],
      isServiceable: true,
      status: "ACT"
    }
  });
  const mall = maybe(() => data.malls.edges[0]?.node);

  React.useEffect(() => {
    resetForm();
  }, [mall]);

  const [saveEditAutoBookingFb] = useSetMallAutoBookingConfig({
    onCompleted: data => {
      if (data?.setMallAutoBookingConfig?.autoBookingError?.length === 0) {
        if (saveIndicator === "fb") {
          notify({
            status: "success",
            text: "Changes saved for Food & Beverage!"
          });
          refetch();
        }
      } else if (data?.setMallAutoBookingConfig?.autoBookingError?.length > 0) {
        notify({
          status: "error",
          text: data?.setMallAutoBookingConfig?.autoBookingError[0].message
        });
      }
    }
  });

  const [saveEditAutoBookingGM] = useSetMallAutoBookingConfig({
    onCompleted: data => {
      if (data?.setMallAutoBookingConfig?.autoBookingError?.length === 0) {
        if (saveIndicator === "gm") {
          notify({
            status: "success",
            text: "Changes saved for General Merchandise!"
          });
          refetch();
        } else if (saveIndicator === "both") {
          notify({
            status: "success",
            text: "Express Fleet changes has been saved successfully."
          });
          refetch();
        }
      } else if (data?.setMallAutoBookingConfig?.autoBookingError?.length > 0) {
        notify({
          status: "error",
          text: data?.setMallAutoBookingConfig?.autoBookingError[0].message
        });
      }
    }
  });

  const handleCourierChange = (field: "fb" | "gm") => (
    e: React.ChangeEvent<{ value: CourierFilter }>
  ) => {
    setCourier({
      ...courier,
      [field]: e.target.value
    });
  };

  const handleBack = (forCancel: boolean = false) => {
    if (forCancel) {
      resetForm();
    } else {
      navigate(configurationOpsMenuUrl);
    }
  };

  const validateNewConfigInput = (
    autoBookingConfig: AutoBookingConfig,
    courier: CourierFilter,
    enabled: boolean
  ) => {
    const tempObj = { autoBookingConfig, courier, enabled };

    const formInput = tempObj?.autoBookingConfig;
    if (!formInput) {
      return false;
    }
    if (
      tempObj.courier === undefined ||
      formInput.readyStatusMinutes === undefined ||
      formInput.collectedStatusMinutes === undefined ||
      formInput.anyStatusMinutes === undefined
    ) {
      return false;
    }

    return true;
  };

  React.useEffect(() => {
    if (fbDefaultValues && fbDefaultValues?.autoBookingConfig) {
      const tempObj = {
        autoBookingConfig: fnbAutoBooking,
        courier: courier.fb,
        enabled: fbEnabled
      };

      const isUpdated =
        JSON.stringify(tempObj.autoBookingConfig) !==
          JSON.stringify(fbDefaultValues.autoBookingConfig) ||
        tempObj.courier !== fbDefaultValues.courier ||
        tempObj.enabled !== fbDefaultValues.enabled;

      setFbFormUpdated(isUpdated);
    } else {
      setFbFormUpdated(
        validateNewConfigInput(fnbAutoBooking, courier.fb, fbEnabled)
      );
    }
  }, [fnbAutoBooking, fbEnabled, courier.fb]);

  React.useEffect(() => {
    if (gmDefaultValues && gmDefaultValues?.autoBookingConfig) {
      const tempObj = {
        autoBookingConfig: gmAutoBooking,
        courier: courier.gm,
        enabled: gmEnabled
      };

      const isUpdated =
        JSON.stringify(tempObj.autoBookingConfig) !==
          JSON.stringify(gmDefaultValues.autoBookingConfig) ||
        tempObj.courier !== gmDefaultValues.courier ||
        tempObj.enabled !== gmDefaultValues.enabled;

      setGmFormUpdated(isUpdated);
    } else {
      setGmFormUpdated(
        validateNewConfigInput(gmAutoBooking, courier.gm, gmEnabled)
      );
    }
  }, [gmAutoBooking, gmEnabled, courier.gm]);

  const enableSaveBtn = editOn && (fbFormUpdated || gmFormUpdated);

  const handleSave = () => {
    let _saveIndicator = "";
    if (fbFormUpdated && gmFormUpdated) {
      _saveIndicator = "both";
    } else if (fbFormUpdated) {
      _saveIndicator = "fb";
    } else if (gmFormUpdated) {
      _saveIndicator = "gm";
    }
    setSaveIndicator(_saveIndicator);

    if (_saveIndicator === "fb" || _saveIndicator === "both") {
      saveEditAutoBookingFb({
        variables: {
          input: {
            anyStatusMinutes: fnbAutoBooking?.anyStatusMinutes,
            collectedStatusMinutes: fnbAutoBooking?.collectedStatusMinutes,
            courier: courier.fb,
            enabled: fbEnabled,
            readyStatusMinutes: fnbAutoBooking?.readyStatusMinutes,
            storeType: StoreTypeEnum.FOOD_AND_BEVERAGES
          },
          mallId: mall.id
        }
      });
    }

    if (_saveIndicator === "gm" || _saveIndicator === "both") {
      saveEditAutoBookingGM({
        variables: {
          input: {
            anyStatusMinutes: gmAutoBooking?.anyStatusMinutes,
            collectedStatusMinutes: gmAutoBooking?.collectedStatusMinutes,
            courier: courier.gm,
            enabled: gmEnabled,
            readyStatusMinutes: gmAutoBooking?.readyStatusMinutes,
            storeType: StoreTypeEnum.GENERAL_MERCHANDISE
          },
          mallId: mall.id
        }
      });
    }
  };

  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div className={classes.pageHeader}>
        <div
          style={{
            display: "flex",
            flexDirection: "row"
          }}
        >
          <PageBackNavigation onBack={() => handleBack()} />
          <PageHeader title={intl.formatMessage(sectionNames.expressFleet)} />
        </div>

        <MoreActions
          id={"expressFleetListPageMoreActions"}
          items={[
            {
              action: () =>
                navigate(
                  auditLogsExpressFleet +
                    mall?.id +
                    "?" +
                    "mall=" +
                    encodeURIComponent(mall?.name)
                ),
              id: "expressFleetListPageViewActivityLogs",
              label: "View Activity Logs"
            }
          ]}
        />
      </div>
      <Grid>
        <Typography className={classes.mallNameLabel}>{mall?.name}</Typography>

        <div className={classes.inputCard}>
          <ExpressFleetOpsFormCard
            formTitle={"Food & Beverage"}
            switchEnabled={fbEnabled}
            switchOnChange={() => setFbEnabled(!fbEnabled)}
            disabled={!editOn}
            selectedCourier={courier.fb}
            selectedCourierOnChange={handleCourierChange("fb")}
            courierChoices={expressFleetCourierChoices}
            readyStatusMinutes={fnbAutoBooking?.readyStatusMinutes}
            collectedStatusMinutes={fnbAutoBooking?.collectedStatusMinutes}
            anyStatusMinutes={fnbAutoBooking?.anyStatusMinutes}
            readyStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  readyStatusMinutes: null
                });
              } else {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  readyStatusMinutes: Number(e.target.value)
                });
              }
            }}
            collectedStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  collectedStatusMinutes: null
                });
              } else {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  collectedStatusMinutes: Number(e.target.value)
                });
              }
            }}
            anyStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  anyStatusMinutes: null
                });
              } else {
                setFnbAutoBooking({
                  ...fnbAutoBooking,
                  anyStatusMinutes: Number(e.target.value)
                });
              }
            }}
          />

          <ExpressFleetOpsFormCard
            formTitle={"General Merchandise"}
            switchEnabled={gmEnabled}
            switchOnChange={() => setGmEnabled(!gmEnabled)}
            disabled={!editOn}
            selectedCourier={courier.gm}
            selectedCourierOnChange={handleCourierChange("gm")}
            courierChoices={expressFleetCourierChoices}
            readyStatusMinutes={gmAutoBooking?.readyStatusMinutes}
            collectedStatusMinutes={gmAutoBooking?.collectedStatusMinutes}
            anyStatusMinutes={gmAutoBooking?.anyStatusMinutes}
            readyStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  readyStatusMinutes: null
                });
              } else {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  readyStatusMinutes: Number(e.target.value)
                });
              }
            }}
            collectedStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  collectedStatusMinutes: null
                });
              } else {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  collectedStatusMinutes: Number(e.target.value)
                });
              }
            }}
            anyStatusMinutesOnChange={e => {
              if (e.target.value === "") {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  anyStatusMinutes: null
                });
              } else {
                setGmAutoBooking({
                  ...gmAutoBooking,
                  anyStatusMinutes: Number(e.target.value)
                });
              }
            }}
          />
        </div>
      </Grid>

      <SaveButtonBar
        state={"default"}
        showCancelButton={editOn}
        onCancel={() => handleBack(editOn)}
        onSave={() => handleSave()}
        forceDocked={true}
        disableEditBtn={editOn}
        onEdit={() => setEditOn(!editOn)}
        saveButtonVariant={true}
        disabled={!enableSaveBtn}
        disableAlternateButton={!enableSaveBtn}
        cancelButtonVariant={true}
        cancelButtonOutlined={true}
      />
    </Container>
  );
};

export default ExpressFleetOpsPage;
